import { InputWithLabel } from '@adotel/components'
import { Button, CircularProgress } from '@material-ui/core'

import { getTranslate } from '../../../../localization'
import useStyles from './styles/index.styles'

type CodeEntryProps = {
  code: string
  disabled: boolean
  checking: boolean
  onChange: (value: string) => void
  onApply: () => void
}

/**
 * The code field and the action that checks it.
 *
 * `InputWithLabel` rather than a raw MUI `TextField`: the shared component is the portal's
 * standard (75 screens against 13 on raw TextField, which the conventions mark Legacy) and
 * owns label placement, error text, RTL and Persian-digit handling. Re-implementing those
 * per screen is exactly what the two-dictionary problem makes expensive to unpick later.
 *
 * The button is CONTAINED primary, matching the portal's other Apply — the filter panel's
 * (portal-ux-conventions.md:226). It was outlined, which reads as secondary next to a field
 * it is the only action for.
 */
const CodeEntry = ({
  code,
  disabled,
  checking,
  onChange,
  onApply
}: CodeEntryProps) => {
  const classes = useStyles()
  const pendingCode = code.trim()

  return (
    <div className={classes.codeFieldRow}>
      <InputWithLabel
        className={classes.codeField}
        disabled={disabled}
        fullWidth
        inputSize="small"
        label={getTranslate('Discount code')}
        noHelperText
        // Submitting from the keyboard is the natural gesture in a one-field form, and
        // without it the only way to check a code is to leave the field for the button.
        onKeyDown={(event: React.KeyboardEvent) => {
          if (
            event.key === 'Enter' &&
            !disabled &&
            !checking &&
            pendingCode !== ''
          ) {
            onApply()
          }
        }}
        // The 128 cap lives here because InputWithLabel exposes no inputProps to put it
        // on the underlying input. Enforced on the way in rather than dropped: the field
        // takes a pasted value, and a code long enough to matter is a malformed one.
        //
        // NOT equivalent to the native maxlength it replaces, and the difference is worth
        // knowing before copying this elsewhere. Native maxlength is composition-aware —
        // it defers truncation until compositionend and never touches an in-progress
        // composed string. Re-setting a controlled value on every keystroke can desync an
        // active IME session. Acceptable HERE because a discount code is short ASCII, and
        // the truncation only bites at 128 characters, which no composition reaches. On a
        // free-text field it would matter, so do not lift this pattern verbatim.
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          onChange(event.currentTarget.value.slice(0, 128))
        }
        value={code}
      />
      <Button
        className={classes.applyButton}
        color="primary"
        variant="contained"
        // Explicit, not on-change or debounced. A code is an assertion the booker makes;
        // checking half-typed input reports failures for codes nobody entered.
        disabled={disabled || checking || pendingCode === ''}
        // The label stays put while the spinner replaces the visible text. Without it the
        // accessible name disappears mid-interaction, so a screen reader announces an
        // unnamed disabled button at exactly the moment the user wants to know what is
        // happening. aria-busy says which moment that is.
        aria-label={getTranslate('Apply')}
        aria-busy={checking}
        onClick={onApply}>
        {checking ? <CircularProgress size={16} /> : getTranslate('Apply')}
      </Button>
    </div>
  )
}

export default CodeEntry
